import { useMutation } from '@platform/api-client';
import type {
  Operation,
  ResponseBody,
  ShippingClient,
  ShippingOperation,
  TypedClient,
} from '@platform/openapi';
import { usePageContext } from '../usePageContext.ts';
import type { DispatchFees } from './dispatch-fees.ts';
import {
  hasPrintableLabel,
  isCancelable,
  STOCK_RESOURCES,
  type CancelReason,
  type Order,
  type OrderStatus,
} from './orders.ts';

export const BULK_KINDS = ['confirm', 'dispatch', 'labels', 'cancel'] as const;
export type BulkKind = (typeof BULK_KINDS)[number];

export type BulkResults = ResponseBody<Operation<'/admin/orders/bulk/confirm', 'post'>>['results'];
export type LabelSheet = ResponseBody<ShippingOperation<'/admin/shipments/label-sheet', 'post'>>;

export type StatusBulkKind = Exclude<BulkKind, 'labels'>;

export type BulkOutcome =
  | { readonly kind: StatusBulkKind; readonly results: BulkResults; readonly skipped: number }
  | { readonly kind: 'labels'; readonly sheet: LabelSheet; readonly skipped: number };

export type BulkRequest =
  | { readonly kind: 'confirm' | 'labels'; readonly orders: readonly Order[] }
  | {
      readonly kind: 'dispatch';
      readonly orders: readonly Order[];
      readonly carrierCode: string;
      readonly fees: DispatchFees;
      readonly idempotencyKey: string;
    }
  | { readonly kind: 'cancel'; readonly orders: readonly Order[]; readonly reason: CancelReason };

const ELIGIBLE_STATUS: Readonly<Record<'confirm' | 'dispatch', OrderStatus>> = {
  confirm: 'PENDING',
  dispatch: 'CONFIRMED',
};

const isEligible: Readonly<Record<BulkKind, (order: Order) => boolean>> = {
  confirm: (order) => order.status === ELIGIBLE_STATUS.confirm,
  dispatch: (order) => order.status === ELIGIBLE_STATUS.dispatch,
  labels: hasPrintableLabel,
  cancel: isCancelable,
};

export const eligibleFor = (kind: BulkKind, orders: readonly Order[]): readonly Order[] =>
  orders.filter(isEligible[kind]);

const targetsOf = (orders: readonly Order[]) =>
  orders.map((order) => ({ orderId: order.id, version: order.version }));

export const useBulkOrderActions = (api: TypedClient, shipping: ShippingClient) => {
  const { invalidate } = usePageContext();
  const run = async (request: BulkRequest): Promise<BulkOutcome> => {
    const eligible = eligibleFor(request.kind, request.orders);
    const skipped = request.orders.length - eligible.length;
    if (request.kind === 'labels') {
      const shipmentIds = eligible.flatMap((order) => order.dispatch.shipmentId ?? []);
      const sheet = await shipping.post('/admin/shipments/label-sheet', { body: { shipmentIds } });
      return { kind: 'labels', sheet, skipped };
    }
    if (request.kind === 'dispatch') {
      const { results } = await api.post('/admin/orders/bulk/dispatch', {
        body: {
          orders: targetsOf(eligible).map((target) => ({
            ...target,
            shippingFee: request.fees[target.orderId] ?? '',
          })),
          carrierCode: request.carrierCode,
        },
        idempotencyKey: request.idempotencyKey,
      });
      return { kind: 'dispatch', results, skipped };
    }
    if (request.kind === 'cancel') {
      const { results } = await api.post('/admin/orders/bulk/cancel', {
        body: { orders: targetsOf(eligible), reason: request.reason },
      });
      return { kind: 'cancel', results, skipped };
    }
    const { results } = await api.post('/admin/orders/bulk/confirm', {
      body: { orders: targetsOf(eligible) },
    });
    return { kind: 'confirm', results, skipped };
  };
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await invalidate(...STOCK_RESOURCES);
    },
  });
};
